// CC-024 part 2 — close a run as paid outside the platform / reopen it. Approvers only.
import { useState } from "react";
import api from "@/lib/api";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  return d?.message || (typeof d === "string" ? d : null) || e.message;
};
const inputCls = "block w-full border border-[#d8dce3] rounded px-2 py-1 text-[12px]";

function CloseForm({ onSubmit, onCancel, busy }) {
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [match, setMatch] = useState(null);
  const [note, setNote] = useState("");
  const ok = reason.trim().length >= 20 && evidence.trim().length >= 3 && match !== null;
  return (
    <div className="mt-2 space-y-2 max-w-[560px]" data-testid="paid-ext-close-form">
      <label className="block text-[11px]">Reason (at least 20 characters)
        <textarea className={inputCls} rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                  data-testid="paid-ext-reason-input" />
      </label>
      <label className="block text-[11px]">Payment evidence (what was paid, where, reference)
        <input className={inputCls} value={evidence} onChange={(e) => setEvidence(e.target.value)}
               data-testid="paid-ext-evidence-input" />
      </label>
      <div className="text-[11px]">Do the computed totals match what was paid?
        <label className="ml-3"><input type="radio" name="paid-ext-match" checked={match === true}
               onChange={() => setMatch(true)} data-testid="paid-ext-match-yes" /> Yes</label>
        <label className="ml-3"><input type="radio" name="paid-ext-match" checked={match === false}
               onChange={() => setMatch(false)} data-testid="paid-ext-match-no" /> No</label>
      </div>
      <label className="block text-[11px]">Note on totals (optional)
        <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)}
               data-testid="paid-ext-totals-note-input" />
      </label>
      <div className="flex gap-2">
        <button className="px-3 py-1.5 rounded bg-[#1c6a99] text-white text-[12px] font-semibold disabled:opacity-50"
                disabled={!ok || busy} data-testid="paid-ext-close-submit"
                onClick={() => onSubmit({ reason: reason.trim(), payment_evidence: evidence.trim(), totals_match: match, totals_note: note.trim() || null })}>
          {busy ? "Closing…" : "Close as paid externally"}
        </button>
        <button className="px-2 py-1.5 text-[12px] text-[color:var(--cc-ink-muted)]" onClick={onCancel}
                data-testid="paid-ext-close-cancel">Cancel</button>
      </div>
    </div>
  );
}

function ClosedState({ run, canAct, call, busy }) {
  const c = run.paid_externally || {};
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-1" data-testid="paid-ext-closed">
      <div className="text-[12.5px] font-semibold text-[#1c6a99]" data-testid="paid-ext-badge">PAID EXTERNALLY · run is frozen</div>
      <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]" data-testid="paid-ext-details">
        By {c.closed_by} · {c.closed_at} · evidence: {c.payment_evidence} · totals match: {c.totals_match ? "yes" : "no"}
      </div>
      {canAct && (
        <div className="flex flex-wrap gap-2 items-end">
          <button className="text-[11.5px] border border-[#cfd4dc] rounded px-2 py-1" disabled={busy}
                  data-testid="paid-ext-csr-mark-btn"
                  onClick={() => window.confirm("Mark this run's CSR sales (still needing payout) as paid outside the platform?")
                    && call("csr-sales/mark-paid-off-system", { confirm: true })}>
            Mark this run's CSR sales paid off-system
          </button>
          {!open ? (
            <button className="text-[11.5px] border border-[#cfd4dc] rounded px-2 py-1" onClick={() => setOpen(true)}
                    data-testid="paid-ext-reopen-open-btn">Reopen</button>
          ) : (
            <>
              <input className="border border-[#d8dce3] rounded px-2 py-1 text-[12px] w-[320px]" value={reason}
                     placeholder="Reason to reopen (at least 20 characters)" onChange={(e) => setReason(e.target.value)}
                     data-testid="paid-ext-reopen-reason-input" />
              <button className="px-3 py-1 rounded bg-[#0e1420] text-white text-[12px] disabled:opacity-50"
                      disabled={busy || reason.trim().length < 20} data-testid="paid-ext-reopen-submit"
                      onClick={() => call("reopen-from-paid-externally", { reason: reason.trim() })}>Reopen run</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function PaidExternallyPanel({ run, slug, canAct, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const closed = (run.status || "").toUpperCase() === "PAID_EXTERNALLY";
  const call = async (path, body) => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(`/payroll/runs/${run.id}/${path}?tenant_slug=${slug}`, body);
      setMsg({ ok: data.marked_count != null ? `Marked ${data.marked_count} CSR sale(s) paid off-system.` : "Saved." });
      setOpen(false);
      onChanged?.();
    } catch (e) {
      setMsg({ err: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  if (!closed && (!canAct || run.approved_at || run.approval_override_at)) return null;
  return (
    <div className="mt-2" data-testid="paid-ext-panel">
      {closed ? <ClosedState run={run} canAct={canAct} call={call} busy={busy} /> : !open ? (
        <button className="text-[11.5px] font-semibold border border-[#7fb3d5] text-[#1c6a99] bg-[#eef6fb] px-2 py-1 rounded"
                onClick={() => setOpen(true)} data-testid="paid-ext-open-btn">Close as paid externally</button>
      ) : (
        <CloseForm busy={busy} onCancel={() => setOpen(false)} onSubmit={(b) => call("close-paid-externally", b)} />
      )}
      {msg?.ok && <div className="text-[11px] text-[#0e6b52] mt-1" data-testid="paid-ext-ok">{msg.ok}</div>}
      {msg?.err && <div className="text-[11px] text-[#991b1b] mt-1" data-testid="paid-ext-error">{String(msg.err)}</div>}
    </div>
  );
}
